import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import TableCell from '@mui/material/TableCell';

import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import useNotification from '../../../components/_hook/useNotification';
import DialogConfirm from '../../../components/_dialog/DialogConfirm';
import { useUnpublishMinisitesMutation } from '../../../../graphql/mutation/minisite/unpublishMinisitesMutation';
import StatusIconButton from '../../../components/_iconButton/StatusIconButton';
import { useDeleteMinisitesMutation } from '../../../../graphql/mutation/minisite/deleteMinisitesMutation';
import CopyIconButton from '../../../components/_iconButton/CopyIconButton';
import { usePublishMinisitesMutation } from '../../../../graphql/mutation/minisite/publishMinisitesMutation';
import DeleteIconButton from '../../../components/_iconButton/DeleteIconButton';
import useRoutes from '../../../components/_hook/useRoutes';
import SuperAdminPermission from '../../../adminComponents/_permission/SuperAdminPermission';

interface IProps {
  item: {
    _id: string;
    status: boolean;
    company: string;
    domain: string[];
  };
  setTableRefreshKey?: (tableRefreshKey: number) => void;
}

export default function MinisiteTableItem(props: IProps) {
  const { t } = useTranslation();
  const { item, setTableRefreshKey } = props;
  const { notification } = useNotification();
  const { path } = useRoutes();
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);
  const [
    deleteMinisitesMutation,
    { loading: isDeletingMinisites },
  ] = useDeleteMinisitesMutation('isSuccess', {
    onCompleted: () => {
      notification.success(
        t('__item___deleted', { item: `${item._id} ${t('minisite')}` })
      );
      if (setTableRefreshKey) {
        setTableRefreshKey(new Date().getTime());
      }
    },
    onError: (e) => {
      notification.error(e.message);
    },
  });
  const [
    publishMinisitesMutation,
    { loading: isPublishingMinisites },
  ] = usePublishMinisitesMutation(
    `updatedMinisites {
				_id
				status
			}`,
    {
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );
  const [
    unpublishMinisitesMutation,
    { loading: isUnpublishingMinisites },
  ] = useUnpublishMinisitesMutation(
    `updatedMinisites {
				_id
				status
			}`,
    {
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  async function onClickPublish() {
    if (item._id) {
      await publishMinisitesMutation({ variables: { _ids: [item._id] } });
    }
  }

  async function onClickUnpublish() {
    if (item._id) {
      await unpublishMinisitesMutation({
        variables: { _ids: [item._id] },
      });
    }
  }

  async function onClickDelete() {
    if (item._id) {
      await deleteMinisitesMutation({ variables: { _ids: [item._id] } });
    }
  }

  return (
    <>
      <DialogConfirm
        title={t('are_you_sure_you_want_to_delete')}
        open={isOpenDeleteDialog}
        onClose={() => setIsOpenDeleteDialog(false)}
        onConfirm={onClickDelete}
      />
      <TableCell align="left">
        <Tooltip title={item._id}>
          <Button
            color="primary"
            component={Link}
            to={path('minisite.detail', {
              docID: item._id,
              companyID: item.company as string,
            })}
          >
            {item._id.substr(0, 6)}
          </Button>
        </Tooltip>
        <CopyIconButton iconHtmlFontSize="0.8rem" copyText={item._id} />
      </TableCell>
      <TableCell align="left">
        {item.domain &&
          item.domain.map((domain) => (
            <React.Fragment key={domain}>
              <Button
                color="primary"
                component={Link}
                to={path('minisite.detail', {
                  docID: item._id,
                  companyID: item.company as string,
                })}
              >
                <Typography>{domain}</Typography>
              </Button>
              <br />
            </React.Fragment>
          ))}
      </TableCell>
      <TableCell align="right">
        <SuperAdminPermission>
          <StatusIconButton
            status={item.status}
            loading={isUnpublishingMinisites || isPublishingMinisites}
            tooltip={`${item.status ? t('publish') : t('unpublish')} (${t(
              'only_super_admin_will_see_this'
            )})`}
            onClick={item.status ? onClickUnpublish : onClickPublish}
          />
        </SuperAdminPermission>
        <DeleteIconButton
          tooltip={t('delete')}
          loading={isDeletingMinisites}
          onClick={() => setIsOpenDeleteDialog(true)}
        />
      </TableCell>
    </>
  );
}
